import { useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useRetreats } from "@/components/site/RetreatDates";

const NIC_OFFSET = 6;
const HOURS = Array.from({ length: 30 }, (_, i) => [6 + Math.floor(i / 2), (i % 2) * 30]);
const fmt = (d, tz) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) });

function slotsFor(day, taken) {
  if (!day) return [];
  const min = Date.now() + 3600e3;
  return HOURS.map(([h, m]) => new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), h + NIC_OFFSET, m)))
    .filter((d) => d.getTime() > min && !taken.has(d.toISOString()));
}

function SlotGrid({ slots, value, onPick }) {
  if (!slots.length) return <p className="text-sm text-slate-500" data-testid="book-call-no-slots">No open times this day — try another date.</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="book-call-slots">
      {slots.map((d) => {
        const iso = d.toISOString();
        return (
          <button key={iso} type="button" onClick={() => onPick(iso)} data-testid={`book-call-slot-${fmt(d, "America/Managua").replace(/\W+/g, "")}`}
            className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${value === iso ? "bg-[#0B2A4A] text-white border-[#0B2A4A]" : "border-[#0B2A4A]/15 text-[#0B2A4A] hover:bg-[#FAF6EE]"}`}>
            {fmt(d)}<span className="block font-normal opacity-70">{fmt(d, "America/Managua")} NI</span>
          </button>
        );
      })}
    </div>
  );
}

export function BookCallDialog({ open, onOpenChange, source = "site", interest: initialInterest = "" }) {
  const retreats = useRetreats();
  const [interest, setInterest] = useState(initialInterest);
  const [day, setDay] = useState();
  const [slot, setSlot] = useState("");
  const [taken, setTaken] = useState(new Set());
  const [f, setF] = useState({ name: "", email: "", whatsapp: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  useEffect(() => { if (open) { api.get("/intro-calls/taken").then((r) => setTaken(new Set(r.data.map((s) => new Date(s).toISOString())))).catch(() => {}); } }, [open]);
  const slots = useMemo(() => slotsFor(day, taken), [day, taken]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const last = new Date(today.getTime() + 59 * 864e5);
  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!slot) return toast.error("Please pick a time");
    setBusy(true);
    try {
      const { data } = await api.post("/intro-calls", { ...f, start: slot, source, interest });
      setDone(data.when);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="book-call-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0B2A4A]">Free 20-minute call with Greg</DialogTitle>
          <DialogDescription>See if becoming a digital nomad is for you. Pick a day and time — Greg confirms on WhatsApp.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-8" data-testid="book-call-success">
            <CheckCircle2 className="h-12 w-12 text-[#1A9AA8] mx-auto" />
            <p className="mt-4 text-lg font-bold text-[#0B2A4A]">Request sent for {new Date(slot).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
            <p className="mt-2 text-sm text-slate-600">({done}). Greg will confirm with you on WhatsApp.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid md:grid-cols-[auto_1fr] gap-6">
            <Calendar mode="single" selected={day} onSelect={(d) => { setDay(d); setSlot(""); }} disabled={[{ before: today }, { after: last }]} className="rounded-xl border border-[#0B2A4A]/10 mx-auto" data-testid="book-call-calendar" />
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#147C88]">{day ? "Times shown in your time zone (and Nicaragua)" : "Pick a date first"}</p>
              {day && <SlotGrid slots={slots} value={slot} onPick={setSlot} />}
              <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="book-call-name" />
              <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="book-call-email" />
              <Input required type="tel" pattern="[0-9+\s\(\)\.\-]{7,}" title="WhatsApp number with country code" placeholder="WhatsApp number (with country code)" value={f.whatsapp} onChange={set("whatsapp")} data-testid="book-call-whatsapp" />
              <select value={interest} onChange={(e) => setInterest(e.target.value)} className="h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="book-call-interest">
                <option value="">I'm interested in… (optional)</option>
                {retreats.map((r) => <option key={r.id} value={r.id}>{`${r.label} · ${r.check_in} · ${r.seats_left} of ${r.max_seats} seats left`}</option>)}
              </select>
              <Input placeholder="Anything Greg should know? (optional)" value={f.note} onChange={set("note")} maxLength={1000} data-testid="book-call-note" />
              <button disabled={busy || !slot} className="rounded-xl bg-[#F08A3C] text-white px-5 py-3 font-semibold hover:bg-[#D96F28] disabled:opacity-60" data-testid="book-call-submit">{busy ? "Booking..." : "Book my free call"}</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BookCallButton({ source, light = false, className = "", testId = "book-call-btn", label = "Book a free 20-minute call", interest = "" }) {
  const [open, setOpen] = useState(false);
  const style = light ? "bg-white text-[#0B2A4A] hover:bg-[#F5ECD7]" : "bg-[#0B2A4A] text-white hover:bg-[#147C88]";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition-colors ${style} ${className}`} data-testid={testId}>
        <PhoneCall className="h-4 w-4" /> {label}
      </button>
      <BookCallDialog open={open} onOpenChange={setOpen} source={source} interest={interest} />
    </>
  );
}

export function BookCallStrip({ source }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl bg-[#F5ECD7] p-4" data-testid={`book-call-strip-${source}`}>
      <CalendarDays className="h-6 w-6 text-[#F08A3C] shrink-0" />
      <p className="text-sm text-[#0B2A4A] flex-1"><strong>Not sure yet?</strong> Book a free 20-minute one-on-one with Greg to see if becoming a digital nomad is for you.</p>
      <BookCallButton source={source} testId={`book-call-btn-${source}`} label="Pick a time" className="py-2 text-sm" />
    </div>
  );
}
